import React, { useState } from 'react';
import { LockKeyhole, Eye, EyeOff } from 'lucide-react';
import Button from '../Ui/Button';
import toastService from '../../services/toastService';
import { useChangePasswordMutation } from '../../app/services/authApi';

import { useNavigate } from 'react-router-dom';
import BackButton from './BackButton';
import { checkPasswordStrength } from '../../utils/password'; // Import the utility

const ChangePassword = () => {
  const navigate = useNavigate();
  const [changePassword] = useChangePasswordMutation();
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [passwordStrength, setPasswordStrength] = useState(null); // New state for strength

  const validateForm = () => {
    let newErrors = {};

    if (!formData.oldPassword.trim()) {
      newErrors.oldPassword = 'Old password is required';
    }

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
    } else {
      const strength = checkPasswordStrength(formData.newPassword);
      setPasswordStrength(strength); // Update strength state for display

      if (!strength.minLen) {
        newErrors.newPassword = newErrors.newPassword
          ? newErrors.newPassword + ', 8 characters minimum'
          : 'Password must be at least 8 characters';
      }
      if (!strength.hasUpperCase) {
        newErrors.newPassword = newErrors.newPassword
          ? newErrors.newPassword + ', at least one uppercase letter'
          : 'Password must contain at least one uppercase letter';
      }
      if (!strength.hasLowerCase) {
        newErrors.newPassword = newErrors.newPassword
          ? newErrors.newPassword + ', at least one lowercase letter'
          : 'Password must contain at least one lowercase letter';
      }
      if (!strength.hasNumber) {
        newErrors.newPassword = newErrors.newPassword
          ? newErrors.newPassword + ', at least one number'
          : 'Password must contain at least one number';
      }
      if (!strength.hasSpecialChar) {
        newErrors.newPassword = newErrors.newPassword
          ? newErrors.newPassword + ', at least one special character'
          : 'Password must contain at least one special character';
      }
    }

    if (formData.confirmPassword !== formData.newPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'newPassword') {
      if (value.length > 0) {
        setPasswordStrength(checkPasswordStrength(value));
      } else {
        setPasswordStrength(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await changePassword(formData).unwrap();
      toastService.success('Password changed successfully!');
      setFormData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      navigate(-1); // Navigate back to the previous page
    } catch (error) {
      toastService.error(error.data?.message || 'Failed to change password');
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center secondary-background px-4">
      <div className="absolute top-12 left-12">
        <BackButton />
      </div>
      <div className="bg-white w-full max-w-md p-8 rounded-2xl shadow-lg border border-color">
        <h2 className="text-2xl font-semibold text-center secondary-text-color mb-6">
          Change Password
        </h2>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium secondary-text-color mb-1">
              Old Password
            </label>
            <div className="relative">
              <LockKeyhole className="absolute left-3 top-3 h-5 w-5 text-primary" />
              <input
                type={showPassword.old ? 'text' : 'password'}
                name="oldPassword"
                value={formData.oldPassword}
                onChange={handleChange}
                className="w-full pl-10 pr-10 p-2 border text-sm rounded-lg focus:outline-primary"
                placeholder="Enter old password"
              />
              {showPassword.old ? (
                <EyeOff
                  className="absolute right-3 top-3 h-5 w-5 text-primary cursor-pointer"
                  onClick={() => togglePasswordVisibility('old')}
                />
              ) : (
                <Eye
                  className="absolute right-3 top-3 h-5 w-5 text-primary cursor-pointer"
                  onClick={() => togglePasswordVisibility('old')}
                />
              )}
            </div>
            {errors.oldPassword && (
              <p className="text-sm text-red-500 mt-1">{errors.oldPassword}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium secondary-text-color mb-1">
              New Password
            </label>
            <div className="relative">
              <LockKeyhole className="absolute left-3 top-3 h-5 w-5 text-primary" />
              <input
                type={showPassword.new ? 'text' : 'password'}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                className="w-full pl-10 pr-10 p-2 border text-sm rounded-lg focus:outline-primary"
                placeholder="Enter new password"
              />
              {showPassword.new ? (
                <EyeOff
                  className="absolute right-3 top-3 h-5 w-5 text-primary cursor-pointer"
                  onClick={() => togglePasswordVisibility('new')}
                />
              ) : (
                <Eye
                  className="absolute right-3 top-3 h-5 w-5 text-primary cursor-pointer"
                  onClick={() => togglePasswordVisibility('new')}
                />
              )}
            </div>
            {passwordStrength && (
              <div className="text-sm mt-2 space-y-1">
                <p className={passwordStrength.minLen ? 'text-green-500' : 'text-red-500'}>
                  {passwordStrength.minLen ? '✓' : '✗'} 8 characters minimum
                </p>
                <p className={passwordStrength.hasUpperCase ? 'text-green-500' : 'text-red-500'}>
                  {passwordStrength.hasUpperCase ? '✓' : '✗'} At least one uppercase letter
                </p>
                <p className={passwordStrength.hasLowerCase ? 'text-green-500' : 'text-red-500'}>
                  {passwordStrength.hasLowerCase ? '✓' : '✗'} At least one lowercase letter
                </p>
                <p className={passwordStrength.hasNumber ? 'text-green-500' : 'text-red-500'}>
                  {passwordStrength.hasNumber ? '✓' : '✗'} At least one number
                </p>
                <p className={passwordStrength.hasSpecialChar ? 'text-green-500' : 'text-red-500'}>
                  {passwordStrength.hasSpecialChar ? '✓' : '✗'} At least one special character
                </p>
              </div>
            )}
            {errors.newPassword && (
              <p className="text-sm text-red-500 mt-1">{errors.newPassword}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium secondary-text-color mb-1">
              Confirm New Password
            </label>
            <div className="relative">
              <LockKeyhole className="absolute left-3 top-3 h-5 w-5 text-primary" />
              <input
                type={showPassword.confirm ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="w-full pl-10 pr-10 p-2 border text-sm rounded-lg focus:outline-primary"
                placeholder="Confirm new password"
              />
              {showPassword.confirm ? (
                <EyeOff
                  className="absolute right-3 top-3 h-5 w-5 text-primary cursor-pointer"
                  onClick={() => togglePasswordVisibility('confirm')}
                />
              ) : (
                <Eye
                  className="absolute right-3 top-3 h-5 w-5 text-primary cursor-pointer"
                  onClick={() => togglePasswordVisibility('confirm')}
                />
              )}
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-red-500 mt-1">{errors.confirmPassword}</p>
            )}
          </div>

          <div className="pt-2 flex justify-center">
            <Button type="submit" className="primary-background white-text w-[40%]">
              Submit
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
